import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Alert, Card } from '@/components/ui';
import { useAuth } from '../../contexts/AuthContext';

const LoginPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { login, devLogin } = useAuth();
  
  // Consistent environment detection with server
  // Priority: REACT_APP_ENVIRONMENT > NODE_ENV > default to 'development'
  const env = process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || 'development';
  const normalizedEnv = env.toLowerCase();
  const validEnvironments = ['development', 'staging', 'training', 'testing', 'production'];
  const releaseStage = validEnvironments.includes(normalizedEnv) ? normalizedEnv : 'development';
  
  const formatEnv = (env: string) => {
    return env.charAt(0).toUpperCase() + env.slice(1).toLowerCase();
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('error') === 'user_not_found') {
      setError('User does not exist. Please contact support.');
    } else if (params.get('error') === 'no_email') {
      setError('No email found in your Microsoft account. Please contact support.');
    } else if (params.get('error') === 'user_disabled') {
      setError('Your account has been disabled. Please contact support.');
    } else if (params.get('error') === 'database_error') {
      setError('A database error occurred. Please try again later or contact support.');
    }
  }, []);

  const handleLogin = () => {
    setLoading(true);
    setError(null);
    login(); // This will redirect to Microsoft login
  };

  const handleDevLogin = async () => {
    const email = window.prompt('Development login – enter the email of the user to log in as:');
    if (!email || !email.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const ok = await devLogin(email.trim());
      if (ok) {
        navigate('/home');
      } else {
        setError(`Could not log in as "${email.trim()}". No portal user found for that email.`);
      }
    } catch (err) {
      setError('Development login failed. Is the server running?');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      style={{
        backgroundImage: 'url("/dawsonroadmaintenancetruck.jpg")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '100vh',
        position: 'relative',
      }}
    >
      {/* Grey overlay */}
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(33, 37, 41, 0.7)',
        }}
      />
      
      {/* Content */}
      <Container fluid className="tw-relative" style={{ zIndex: 1 }}>
        <Row className="tw-h-screen tw-flex tw-justify-center tw-items-center">
          <Col xs={12} sm={10} md={8} lg={6} xl={5} xxl={4}>
            <Card className="tw-shadow-lg tw-border-0">
              <Card.Body className="tw-p-4 md:tw-p-5">
                <div className="tw-text-center tw-mb-4">
                  <img 
                    src={encodeURI(`${process.env.PUBLIC_URL || ''}/WRAP logo no border.png`)} 
                    alt="WRAP Logo" 
                    style={{ 
                      maxWidth: '200px', 
                      marginBottom: '1.5rem',
                      width: '100%',
                      height: 'auto'
                    }} 
                  />
                  {releaseStage !== 'production' && (
                    <div className="tw-text-content-muted tw-mb-3">
                      Environment - {formatEnv(releaseStage)}
                    </div>
                  )}
                  <h2 className="tw-text-2xl tw-font-light tw-mb-3">WRAP</h2>
                  <h2 className="tw-text-2xl tw-font-light tw-mb-3">(Work Reporting and Planning)</h2>
                  <p className="tw-text-content-muted">Please sign in with your work account</p>
                </div>

                {error && <Alert variant="danger" className="tw-mb-3">{error}</Alert>}

                <Button
                  variant="outline-secondary"
                  onClick={handleLogin}
                  disabled={loading}
                  className="tw-w-full tw-flex tw-items-center tw-justify-center tw-py-2 tw-mb-3"
                >
                  <svg className="tw-mr-2" width="20" height="20" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 0H0V10H10V0Z" fill="#F25022"/>
                    <path d="M21 0H11V10H21V0Z" fill="#7FBA00"/>
                    <path d="M10 11H0V21H10V11Z" fill="#00A4EF"/>
                    <path d="M21 11H11V21H21V11Z" fill="#FFB900"/>
                  </svg>
                  {loading ? 'Signing in...' : 'Sign in with Microsoft'}
                </Button>

                {/* Development Login Button - only show in development */}
                {releaseStage === 'development' && (
                  <Button
                    variant="outline-primary"
                    onClick={handleDevLogin}
                    className="tw-w-full tw-py-2"
                  >
                    Development Login
                  </Button>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default LoginPage;
